import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { AuroraBackground } from "@/components/aurora-background";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { seo } from "@/lib/seo";
import { breadcrumb, faqPage } from "@/lib/structured-data";
import { SITE_URL } from "@/lib/seo-constants";

const FAQS = [
  {
    q: "What is this program?",
    a: "The Axy.digital Affiliate Program rewards you for referring customers to Axy.digital—the AI-powered GTM engine that automates research, execution, and optimization across GTM channels.",
  },
  {
    q: "Who can join?",
    a: "Anyone with a professional network of founders, marketers, or B2B startups can join. The program is especially suited for fractional CMOs, marketing consultants, remote closers, and growth advisors.",
  },
  {
    q: "How much do I earn?",
    a: "You earn 20% of every referred customer's lifetime payments, paid monthly. That means as long as your referrals stay subscribed, you keep earning recurring commission.",
  },
  {
    q: "How do I track my referrals?",
    a: "Once approved, you'll receive a personal referral link and a private dashboard to track clicks, signups, and payments in real time.",
  },
  {
    q: "Is there a limit on earnings?",
    a: "No limits. The more customers you refer, the more recurring revenue you earn.",
  },
  {
    q: "How does Axy.digital support affiliates?",
    a: "You'll get access to exclusive affiliate materials, pitch decks, demo scripts, social media copy, and product updates, so you can confidently present Axy.digital to potential clients.",
  },
];

export const Route = createFileRoute("/affiliates")({
  head: () =>
    seo({
      title: "Affiliate Partner Program: Earn 20% Recurring",
      description:
        "Join the Axy.digital Affiliate Program. Earn 20% lifetime recurring commission by introducing your B2B network to our autonomous AI GTM platform.",
      path: "/affiliates",
      type: "website",
      jsonLd: [
        faqPage(FAQS),
        breadcrumb([
          { name: "Home", url: "/" },
          { name: "Affiliates", url: "/affiliates" },
        ]),
      ],
    }),
  component: AffiliatesPage,
});

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.round(value));
}

function AffiliatesPage() {
  const [referrals, setReferrals] = useState(10);

  const { monthly, annual } = useMemo(() => {
    const blended = (1500 / 12) * 0.8 + 249 * 0.2;
    const m = referrals * blended * 0.2;
    return { monthly: m, annual: m * 12 };
  }, [referrals]);

  function handleJoin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const email = (form.elements.namedItem("email") as HTMLInputElement | null)?.value;
    if (!email) return;
    toast.success("You're in — we'll be in touch shortly.");
    form.reset();
  }

  function handleSubscribe(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const email = (form.elements.namedItem("email") as HTMLInputElement | null)?.value;
    if (!email) return;
    toast.success("Subscribed — market reports incoming.");
    form.reset();
  }

  return (
    <article className="text-white">
      {/* Hero & Application */}
      <section className="relative px-6 pt-20 pb-16 overflow-hidden">
        <AuroraBackground />
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/80">
            <span className="size-1.5 rounded-full bg-success" />
            <span className="tracking-wide uppercase">Axy.digital Affiliate Program</span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-balance mb-6">
            Turn Your Network into <span className="text-gradient">Recurring Revenue</span>.
          </h1>
          <p className="text-lg md:text-xl text-white/75 max-w-2xl mx-auto leading-relaxed mb-10">
            Join the Axy.digital Affiliate Program and earn 20% lifetime commission for every team
            you bring on board.
          </p>

          <form onSubmit={handleJoin} className="flex flex-col sm:flex-row gap-2 max-w-xl mx-auto">
            <Input
              type="email"
              name="email"
              required
              maxLength={255}
              placeholder="you@company.com"
              aria-label="Email address"
              className="h-12 bg-white/5 border-white/20 text-white placeholder:text-white/40"
            />
            <Button
              type="submit"
              className="h-12 px-6 btn-gradient hover:btn-gradient-hover text-white border-0"
            >
              Join the Program <ArrowRight className="size-4" />
            </Button>
          </form>
        </div>
      </section>

      {/* Earnings Calculator */}
      <section className="px-6 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-mono uppercase tracking-[0.18em] text-white/50 mb-3">
              Earnings Calculator
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-balance">
              See your compounding earning potential.
            </h2>
          </div>

          <div className="glass-card p-8 md:p-10">
            <div className="mb-8">
              <div className="flex items-baseline justify-between mb-4">
                <label htmlFor="referrals-slider" className="text-sm font-medium text-white/80">
                  Customers Referred
                </label>
                <span className="text-2xl font-semibold tabular-nums text-white">{referrals}</span>
              </div>
              <Slider
                id="referrals-slider"
                min={1}
                max={50}
                step={1}
                value={[referrals]}
                onValueChange={(v) => setReferrals(v[0] ?? 1)}
                aria-label="Number of customers referred"
              />
              <div className="flex justify-between text-xs text-white/40 mt-2 font-mono">
                <span>1</span>
                <span>50</span>
              </div>
            </div>

            <div className="border-t border-white/10 pt-8 space-y-4 text-center">
              <div>
                <p className="text-sm text-white/60 mb-2">Your Estimated Monthly MRR</p>
                <p className="text-5xl md:text-6xl font-semibold tracking-tight text-success tabular-nums">
                  {formatCurrency(monthly)}
                </p>
              </div>
              <p className="text-base text-white/70 tabular-nums">
                Annualized Contract Revenue:{" "}
                <span className="text-white font-medium">{formatCurrency(annual)}</span>/yr pacing
              </p>
              <p className="text-xs text-white/40 max-w-md mx-auto pt-2">
                Based on a blended retention mix: 80% annual contracts ($1,500/yr) and 20% monthly
                subscribers ($249/mo), at a 20% recurring commission.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-mono uppercase tracking-[0.18em] text-white/50 mb-3">FAQ</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
              Everything you need to know.
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((item, i) => (
              <AccordionItem key={item.q} value={`item-${i}`} className="border-b border-white/10">
                <AccordionTrigger className="text-left text-base font-medium text-white hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/70 text-base leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 pb-24 pt-8">
        <div className="max-w-3xl mx-auto glass-card p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-white mb-3 text-balance">
            Get Axy.digital market reports in your inbox.
          </h2>
          <p className="text-white/70 mb-6">
            Subscribe to our mailing list to get Axy.digital market reports directly in your email.
          </p>
          <form
            onSubmit={handleSubscribe}
            className="flex flex-col sm:flex-row gap-2 max-w-xl mx-auto"
          >
            <Input
              type="email"
              name="email"
              required
              maxLength={255}
              placeholder="you@company.com"
              aria-label="Email address"
              className="h-11 bg-white/5 border-white/20 text-white placeholder:text-white/40"
            />
            <Button type="submit" className="h-11 px-6">
              Subscribe
            </Button>
          </form>
        </div>
      </section>
    </article>
  );
}
